import React, { useState, useEffect } from "react";
import { firestoreService } from "@/services/firestoreService";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";

interface Transcript {
  id: string;
  companyId: string;
  companyName?: string;
  ticker?: string;
  quarter: string;
  year: number;
  date: any;
}

interface Company {
  id: string;
  name: string;
  ticker: string;
}

interface MultiEarningsSelectorProps {
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
}

export const MultiEarningsSelector: React.FC<MultiEarningsSelectorProps> = ({
  selectedIds,
  onSelectionChange,
}) => {
  const [transcripts, setTranscripts] = useState<Transcript[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [displayLimit, setDisplayLimit] = useState(100);
  const [hasMore, setHasMore] = useState(false);

  const loadTranscripts = async () => {
    try {
      setLoading(true);
      // Load more than display limit to check if there are more
      const allTranscripts = await firestoreService.getDocuments<Transcript>(
        "transcripts",
        [
          firestoreService.orderBy("date", "desc"),
          firestoreService.limit(displayLimit + 1), // Load one extra to check if more exist
        ]
      );
      
      // Check if there are more transcripts
      setHasMore(allTranscripts.length > displayLimit);
      
      // Only show up to displayLimit
      const transcriptsToShow = allTranscripts.slice(0, displayLimit);

      const transcriptsWithCompany = await Promise.all(
        transcriptsToShow.map(async (transcript) => {
          try {
            const company = await firestoreService.getDocument<Company>(
              "companies",
              transcript.companyId
            );
            return {
              ...transcript,
              companyName: company?.name || "Unknown",
              ticker: company?.ticker || "",
            };
          } catch {
            return {
              ...transcript,
              companyName: "Unknown",
              ticker: "",
            };
          }
        })
      );

      setTranscripts(transcriptsWithCompany);
    } catch (error) {
      console.error("Failed to load transcripts:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTranscripts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayLimit]);

  const filteredTranscripts = searchQuery
    ? transcripts.filter(
        (t) =>
          t.companyName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          t.ticker?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          `${t.quarter} ${t.year}`.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : transcripts;

  const MAX_SELECTION = 5;

  const toggleSelection = (id: string) => {
    if (selectedIds.includes(id)) {
      onSelectionChange(selectedIds.filter((selectedId) => selectedId !== id));
    } else {
      if (selectedIds.length >= MAX_SELECTION) {
        // Show a subtle notification (could be enhanced with toast)
        alert(`You can select up to ${MAX_SELECTION} earnings calls for comparison.`);
        return;
      }
      onSelectionChange([...selectedIds, id]);
    }
  };

  const selectedTranscripts = transcripts.filter((t) => selectedIds.includes(t.id));

  // Sort selected transcripts by date (most recent first)
  const sortedSelected = [...selectedTranscripts].sort((a, b) => {
    const dateA = a.date?.toDate?.() || a.date || new Date(0);
    const dateB = b.date?.toDate?.() || b.date || new Date(0);
    return new Date(dateB).getTime() - new Date(dateA).getTime();
  });

  const formatDate = (date: any) => {
    if (!date) return "Unknown date";
    const d = date?.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Input
          type="text"
          placeholder="Search by company, ticker, or quarter..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="flex-1"
        />
        {selectedIds.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onSelectionChange([])}
            className="whitespace-nowrap"
          >
            Clear All
          </Button>
        )}
      </div>

      {selectedTranscripts.length > 0 && (
        <Card className="p-4 bg-gradient-to-r from-blazerex-green/10 to-blazerex-green/5 border-2 border-blazerex-green/30">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <svg
                className="w-5 h-5 text-blazerex-green"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                />
              </svg>
              <span className="body-text font-semibold text-blazerex-green">
                Comparing {selectedTranscripts.length} Earnings Call{selectedTranscripts.length > 1 ? "s" : ""}
              </span>
            </div>
          </div>
          <div className="space-y-2">
            {sortedSelected.map((transcript, index) => (
              <div
                key={transcript.id}
                className="px-3 py-2 bg-white rounded-lg border border-blazerex-green/50 flex items-center justify-between hover:border-blazerex-green transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-blazerex-green text-white flex items-center justify-center text-xs font-bold">
                    {index + 1}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="body-text font-mono font-bold text-blazerex-green">
                        {transcript.ticker}
                      </span>
                      <span className="body-text font-medium">{transcript.companyName}</span>
                    </div>
                    <div className="text-xs text-gray-600 mt-0.5">
                      {transcript.quarter} {transcript.year} • {formatDate(transcript.date)}
                    </div>
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleSelection(transcript.id);
                  }}
                  className="text-gray-400 hover:text-red-600 transition-colors p-1"
                  title="Remove from comparison"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            ))}
          </div>
          {selectedTranscripts.length > 1 && (
            <div className="mt-3 pt-3 border-t border-blazerex-green/20">
              <p className="text-xs text-gray-600">
                💡 Tip: Ask questions like "Compare revenue growth" or "What changed between these quarters?"
              </p>
            </div>
          )}
        </Card>
      )}

      <div>
        <div className="flex items-center justify-between mb-2 px-1">
          <span className="text-sm font-medium text-gray-700">
            Available Earnings Calls ({filteredTranscripts.length})
          </span>
          {filteredTranscripts.length > 0 && (
            <span className="text-xs text-gray-500">
              Select up to {MAX_SELECTION} to compare
            </span>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto space-y-2 border rounded-lg p-2 bg-gray-50">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blazerex-green mx-auto"></div>
              <p className="text-sm text-gray-500 mt-2">Loading earnings calls...</p>
            </div>
          ) : filteredTranscripts.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-sm text-gray-500">
                {searchQuery ? "No earnings calls found matching your search." : "No earnings calls available."}
              </p>
            </div>
          ) : (
          filteredTranscripts.map((transcript) => {
            const isSelected = selectedIds.includes(transcript.id);
            return (
              <Card
                key={transcript.id}
                hover
                className={`cursor-pointer p-3 transition-all ${
                  isSelected
                    ? "border-2 border-blazerex-green bg-blazerex-green bg-opacity-10 shadow-md"
                    : "border border-gray-200 hover:border-blazerex-green/50"
                }`}
                onClick={() => toggleSelection(transcript.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="body-text font-mono text-blazerex-green font-bold text-base">
                        {transcript.ticker}
                      </span>
                      <span className="sub-header text-sm">{transcript.companyName}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-600">
                      <span className="font-medium">{transcript.quarter} {transcript.year}</span>
                      <span>•</span>
                      <span>{formatDate(transcript.date)}</span>
                    </div>
                  </div>
                  <div className="ml-3">
                    {isSelected ? (
                      <div className="w-6 h-6 bg-blazerex-green rounded-full flex items-center justify-center shadow-sm">
                        <svg
                          className="w-4 h-4 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={3}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                    ) : (
                      <div className="w-6 h-6 border-2 border-gray-300 rounded-full flex items-center justify-center">
                        <div className="w-2 h-2 rounded-full bg-gray-300"></div>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            );
          })
          )}
        </div>
        {hasMore && (
          <div className="mt-4 text-center">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDisplayLimit(displayLimit + 50)}
              disabled={loading}
            >
              Load More ({displayLimit} shown)
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

